import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const payload = isLogin
        ? { email, password }
        : { name, email, password, phone_number: phoneNumber || null };

      const response = await axios.post(`${BACKEND_URL}${endpoint}`, payload);
      const { access_token, user } = response.data;

      onLogin(access_token, user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-stone-900">
              My Budget Tracker
            </h1>
            <p className="mt-2 text-base font-body text-stone-600">
              Track your finances with precision and clarity
            </p>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-heading font-medium text-stone-900">
                {isLogin ? 'Sign In' : 'Create Account'}
              </h2>
              <p className="mt-1 text-sm font-body text-stone-500">
                {isLogin
                  ? 'Enter your credentials to access your account'
                  : 'Get started with your financial tracking'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="font-body text-sm font-medium">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-1 font-mono"
                    data-testid="name-input"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="font-body text-sm font-medium">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 font-mono"
                  data-testid="email-input"
                />
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="phone" className="font-body text-sm font-medium">
                    Phone Number <span className="text-stone-400">(optional)</span>
                  </Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+61 4XX XXX XXX"
                      className="pl-10 font-mono"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="password" className="font-body text-sm font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 font-mono"
                  data-testid="password-input"
                />
                {isLogin && (
                  <div className="mt-2 text-right">
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-10 font-medium"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm font-body text-stone-600 hover:text-stone-900 transition-colors"
                data-testid="toggle-auth-mode"
              >
                {isLogin
                  ? "Don't have an account? Sign up"
                  : 'Already have an account? Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="hidden lg:block flex-1 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1554224311-beee460ae6ba?w=1200&q=80)',
        }}
      />
    </div>
  );
};

export default Login;
